/**
 * React Query hooks for System Task Schedules
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { systemTaskSchedulesApi } from '@/lib/api/systemTaskSchedules';
import type {
  SystemTaskScheduleCreateRequest,
  SystemTaskScheduleUpdateRequest,
  SystemTaskScheduleListParams,
  SystemTaskExecutionListParams,
} from '@/types/systemTaskSchedule';

// Query keys
export const systemTaskScheduleKeys = {
  all: ['system-task-schedules'] as const,
  lists: () => [...systemTaskScheduleKeys.all, 'list'] as const,
  list: (params?: SystemTaskScheduleListParams) => [...systemTaskScheduleKeys.lists(), params] as const,
  details: () => [...systemTaskScheduleKeys.all, 'detail'] as const,
  detail: (id: string) => [...systemTaskScheduleKeys.details(), id] as const,
  availableTypes: () => [...systemTaskScheduleKeys.all, 'available-types'] as const,
  executions: () => [...systemTaskScheduleKeys.all, 'executions'] as const,
  scheduleExecutions: (scheduleId: string, params?: SystemTaskExecutionListParams) =>
    [...systemTaskScheduleKeys.executions(), 'schedule', scheduleId, params] as const,
  allExecutions: (params?: SystemTaskExecutionListParams) =>
    [...systemTaskScheduleKeys.executions(), 'all', params] as const,
  execution: (id: string) => [...systemTaskScheduleKeys.executions(), 'detail', id] as const,
};

/**
 * Hook for listing system task schedules
 */
export function useSystemTaskScheduleList(params?: SystemTaskScheduleListParams) {
  return useQuery({
    queryKey: systemTaskScheduleKeys.list(params),
    queryFn: () => systemTaskSchedulesApi.list(params),
  });
}

/**
 * Hook for getting a single system task schedule
 */
export function useSystemTaskSchedule(scheduleId: string) {
  return useQuery({
    queryKey: systemTaskScheduleKeys.detail(scheduleId),
    queryFn: () => systemTaskSchedulesApi.get(scheduleId),
    enabled: !!scheduleId,
  });
}

/**
 * Hook for getting available system task types
 */
export function useSystemTaskAvailableTypes() {
  return useQuery({
    queryKey: systemTaskScheduleKeys.availableTypes(),
    queryFn: () => systemTaskSchedulesApi.getAvailableTypes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for creating a system task schedule
 */
export function useCreateSystemTaskSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SystemTaskScheduleCreateRequest) => systemTaskSchedulesApi.create(data),
    onSuccess: (schedule) => {
      queryClient.invalidateQueries({ queryKey: systemTaskScheduleKeys.lists() });
      toast.success(`系统任务 "${schedule.name}" 已创建`);
    },
    onError: (error: Error) => {
      toast.error(`创建失败: ${error.message}`);
    },
  });
}

/**
 * Hook for updating a system task schedule
 */
export function useUpdateSystemTaskSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SystemTaskScheduleUpdateRequest }) =>
      systemTaskSchedulesApi.update(id, data),
    onSuccess: (schedule) => {
      queryClient.invalidateQueries({ queryKey: systemTaskScheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: systemTaskScheduleKeys.detail(schedule.id) });
      toast.success(`系统任务 "${schedule.name}" 已更新`);
    },
    onError: (error: Error) => {
      toast.error(`更新失败: ${error.message}`);
    },
  });
}

/**
 * Hook for deleting a system task schedule
 */
export function useDeleteSystemTaskSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => systemTaskSchedulesApi.delete(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: systemTaskScheduleKeys.lists() });
      toast.success('系统任务已删除');
    },
    onError: (error: Error) => {
      toast.error(`删除失败: ${error.message}`);
    },
  });
}

/**
 * Hook for toggling system task schedule enabled state
 */
export function useToggleSystemTaskSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled?: boolean }) =>
      systemTaskSchedulesApi.toggle(id, enabled),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: systemTaskScheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: systemTaskScheduleKeys.detail(response.id) });
      toast.success(response.is_enabled ? '已启用' : '已禁用');
    },
    onError: (error: Error) => {
      toast.error(`操作失败: ${error.message}`);
    },
  });
}

/**
 * Hook for manually triggering a system task schedule
 */
export function useRunSystemTaskScheduleNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => systemTaskSchedulesApi.runNow(scheduleId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: systemTaskScheduleKeys.lists() });
      // Also invalidate executions
      queryClient.invalidateQueries({ queryKey: systemTaskScheduleKeys.executions() });
      if (response.success) {
        toast.success(`任务执行成功 (${response.duration_ms}ms)`);
      } else {
        toast.warning(`任务执行完成但有错误`);
      }
    },
    onError: (error: Error) => {
      toast.error(`执行失败: ${error.message}`);
    },
  });
}

// ============================================================================
// Execution History Hooks
// ============================================================================

/**
 * Hook for listing executions for a specific schedule
 */
export function useScheduleExecutions(
  scheduleId: string,
  params?: Omit<SystemTaskExecutionListParams, 'schedule_id'>
) {
  return useQuery({
    queryKey: systemTaskScheduleKeys.scheduleExecutions(scheduleId, params),
    queryFn: () => systemTaskSchedulesApi.getScheduleExecutions(scheduleId, params),
    enabled: !!scheduleId,
  });
}

/**
 * Hook for listing all executions
 */
export function useAllExecutions(params?: SystemTaskExecutionListParams) {
  return useQuery({
    queryKey: systemTaskScheduleKeys.allExecutions(params),
    queryFn: () => systemTaskSchedulesApi.listAllExecutions(params),
  });
}

/**
 * Hook for getting a single execution
 */
export function useExecution(executionId: string) {
  return useQuery({
    queryKey: systemTaskScheduleKeys.execution(executionId),
    queryFn: () => systemTaskSchedulesApi.getExecution(executionId),
    enabled: !!executionId,
  });
}
